import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePool } from '../context/PoolContext';
import { getCurrentEvent } from '../services/api';
import pickNPrayLogo from '../Logos/LogoNoText cropped.png';

const PrimaryRow = ({ to, icon, label, active, badge, onClick }) => (
  <Link to={to} className={`sidebar-row ${active ? 'sidebar-row-active' : ''}`} onClick={onClick}>
    <span className="sidebar-row-icon">{icon}</span>
    <span className="sidebar-row-label">{label}</span>
    {badge}
    {active && (
      <svg className="sidebar-row-chevron" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 18 15 12 9 6" />
      </svg>
    )}
  </Link>
);

const SecondaryRow = ({ to, label, active, external }) => {
  if (external) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" className="sidebar-secondary-row">
        {label}
      </a>
    );
  }
  return (
    <Link to={to} className={`sidebar-secondary-row ${active ? 'sidebar-secondary-row-active' : ''}`}>
      {label}
    </Link>
  );
};

function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const { pools, selectedPool, selectedPoolId, setSelectedPoolId, selectedPoolScore, scoresByPool, totalUnread } = usePool();
  const [poolMenuOpen, setPoolMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [isLive, setIsLive] = useState(false);
  const poolMenuRef = useRef(null);
  const userMenuRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    getCurrentEvent()
      .then((res) => setIsLive(res.data?.status === 'locked'))
      .catch(() => {});
  }, [isAuthenticated, location.pathname]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (poolMenuRef.current && !poolMenuRef.current.contains(e.target)) setPoolMenuOpen(false);
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isActive = (path) => location.pathname === path;
  const isActivePrefix = (prefix) => location.pathname.startsWith(prefix);

  const handleLogout = async () => {
    setUserMenuOpen(false);
    await logout();
    navigate('/');
  };

  return (
    <aside className="sidebar">
      <Link to={isAuthenticated ? '/dashboard' : '/'} className="sidebar-brand">
        <img src={pickNPrayLogo} alt="" />
        <span className="sidebar-wordmark" aria-label="Pick 'N Pray">
          <span>Pick 'N</span>
          <span>Pray</span>
        </span>
      </Link>

      {isAuthenticated && pools.length > 0 && (
        <div className="sidebar-pool-selector" ref={poolMenuRef}>
          <div className="sidebar-section-label">Active pool</div>
          <button
            type="button"
            className="sidebar-pool-trigger"
            onClick={() => setPoolMenuOpen((v) => !v)}
            aria-expanded={poolMenuOpen}
          >
            <span className="sidebar-pool-mark" aria-hidden="true">
              {(selectedPool?.name || 'P').trim().charAt(0).toUpperCase()}
            </span>
            <div className="sidebar-pool-info">
              <div className="sidebar-pool-name">{selectedPool?.name || 'Select Pool'}</div>
              <div className="sidebar-pool-score">
                {selectedPoolScore?.total_points ?? 0} pts
              </div>
            </div>
            <svg className={`sidebar-pool-chevron ${poolMenuOpen ? 'open' : ''}`} viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {poolMenuOpen && (
            <div className="sidebar-pool-menu">
              {pools.map((p) => {
                const score = scoresByPool[p.id]?.total_points ?? 0;
                const isSelected = p.id === selectedPoolId;
                return (
                  <button
                    key={p.id}
                    type="button"
                    className={`sidebar-pool-option ${isSelected ? 'sidebar-pool-option-selected' : ''}`}
                    onClick={() => {
                      setSelectedPoolId(p.id);
                      setPoolMenuOpen(false);
                    }}
                  >
                    <span className="sidebar-pool-option-name">{p.name}</span>
                    <span className="sidebar-pool-option-score">{score} pts</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {isAuthenticated ? (
        <nav className="sidebar-nav">
          <PrimaryRow
            to="/dashboard"
            active={isActive('/dashboard')}
            label="Dashboard"
            icon={
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
            }
          />
          <PrimaryRow
            to="/live"
            active={isActive('/live')}
            label="Leaderboards"
            badge={isLive && <span className="sidebar-row-live">LIVE</span>}
            icon={
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 20V10" />
                <path d="M12 20V4" />
                <path d="M6 20v-6" />
              </svg>
            }
          />
          <PrimaryRow
            to="/corner-chat"
            active={isActive('/corner-chat')}
            label="Corner Chat"
            badge={totalUnread > 0 && (
              <span className="sidebar-row-unread">{totalUnread > 9 ? '9+' : totalUnread}</span>
            )}
            icon={
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            }
          />
          <PrimaryRow
            to={selectedPoolId ? `/pool/${selectedPoolId}/picks` : '/dashboard'}
            active={isActivePrefix('/pool/') && location.pathname.endsWith('/picks')}
            label="My Picks"
            icon={
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-4" />
                <rect x="9" y="2" width="6" height="4" rx="1" ry="1" />
                <path d="M9 14l2 2 4-4" />
              </svg>
            }
          />
          <PrimaryRow
            to="/badges"
            active={isActive('/badges')}
            label="Badges"
            icon={
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="8" r="7" />
                <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
              </svg>
            }
          />
          <PrimaryRow
            to="/lock-sheet"
            active={isActive('/lock-sheet')}
            label="Lock Sheet"
            icon={
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            }
          />
          <PrimaryRow
            to="/join"
            active={isActive('/join') || isActive('/create-pool')}
            label="Join / Create Pool"
            icon={
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="16" />
                <line x1="8" y1="12" x2="16" y2="12" />
              </svg>
            }
          />
        </nav>
      ) : (
        <nav className="sidebar-nav">
          <PrimaryRow
            to="/"
            active={isActive('/')}
            label="Home"
            icon={
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
            }
          />
          <PrimaryRow
            to="/faq"
            active={isActive('/faq')}
            label="FAQ"
            icon={
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            }
          />
        </nav>
      )}

      <div className="sidebar-secondary">
        {isAuthenticated && <SecondaryRow to="/faq" label="FAQ" active={isActive('/faq')} />}
        <SecondaryRow to="/privacy" label="Privacy" active={isActive('/privacy')} />
        <SecondaryRow to="/terms" label="Terms" active={isActive('/terms')} />
      </div>

      <div className="sidebar-socials">
        <a href="https://x.com/picknpraymma" target="_blank" rel="noopener noreferrer" title="X">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
          </svg>
        </a>
        <a href="https://www.instagram.com/picknpray_mma/" target="_blank" rel="noopener noreferrer" title="Instagram">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
          </svg>
        </a>
      </div>

      <div className="sidebar-footer">
        {isAuthenticated ? (
          <div className="sidebar-user" ref={userMenuRef}>
            <button
              type="button"
              className="sidebar-user-trigger"
              onClick={() => setUserMenuOpen((v) => !v)}
            >
              <div className="sidebar-user-avatar">{(user?.username || '?').charAt(0).toUpperCase()}</div>
              <div className="sidebar-user-info">
                <div className="sidebar-user-name">{user?.username || 'User'}</div>
                <div className="sidebar-user-meta">View profile</div>
              </div>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {userMenuOpen && (
              <div className="sidebar-user-menu">
                <Link to="/profile" className="sidebar-user-menu-item" onClick={() => setUserMenuOpen(false)}>
                  Profile
                </Link>
                <button type="button" className="sidebar-user-menu-item sidebar-user-menu-item-danger" onClick={handleLogout}>
                  Log Out
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            <Link to="/register" className="sidebar-cta">Sign Up</Link>
            <Link to="/login" className="sidebar-cta-secondary">Sign In</Link>
          </>
        )}
      </div>
    </aside>
  );
}

export default Sidebar;
